import { generateUUID } from '../../../utils/uuid';
import { CSEP_STATUS, PRE_ENTRY_QUESTIONS, SPECIAL_REQUIREMENTS, VERIFICATION_ITEMS, AIR_CONTAMINANTS } from './constants';
import { localDateTimeString } from '../../../utils/localDate';

/**
 * Creates a new CSEP record skeleton with all required fields
 */
export function createCsepSkeleton(overrides = {}) {
    const now = new Date().toISOString();

    // Initialize pre-entry checklist answers (all null initially)
    const preEntryAnswers = {};
    PRE_ENTRY_QUESTIONS.forEach(q => {
        preEntryAnswers[q.id] = null; // null = not answered, 'yes', 'no', 'na'
    });

    // Initialize special requirements (required/verified checkboxes)
    const specialRequirements = {};
    SPECIAL_REQUIREMENTS.forEach(req => {
        specialRequirements[req.id] = { required: false, verified: false };
    });

    // Initialize verifications (with signature support)
    const verifications = {};
    VERIFICATION_ITEMS.forEach(item => {
        verifications[item.id] = { date: '', signature: '', signedBy: '' };
    });

    // Initialize air monitoring readings (3 checks)
    const airMonitoringReadings = {};
    AIR_CONTAMINANTS.forEach(contaminant => {
        airMonitoringReadings[contaminant.id] = {
            check1: { value: '', time: '' },
            check2: { value: '', time: '' },
            check3: { value: '', time: '' }
        };
    });

    return {
        uuid: generateUUID(),
        status: CSEP_STATUS.NEW,
        createdAt: now,
        updatedAt: now,

        // Step 1: Header + Pre-Entry Checklist (Attachment 7A)
        stepOne: {
            // Header info
            mode: 'job', // 'job' or 'location'
            jobId: null,
            jobCode: '',
            jobName: '',
            location: '',
            dateTime: localDateTimeString(), // Combined date and time (local, not UTC)
            entrySupervisorId: null,
            entrySupervisorName: '',
            entrySupervisorPhone: '',
            entrySupervisorType: 'employee', // 'employee' or 'temp'
            // Pre-Entry Checklist answers
            preEntryAnswers,
            preEntryNote: ''
        },

        // Step 2: Entry Permit (Attachment 7B) - All sections combined
        stepTwo: {
            // Description Section
            project: '',
            supervisor: '', // Manager from Job
            subcontractor: '',
            permitType: 'permit', // 'non-permit' or 'permit'
            confinedSpaceLocation: '',
            entryDateTime: '',
            entryAmPm: 'AM',
            confinedSpaceTypes: [], // array of selected type ids
            confinedSpaceTypeOther: '',
            locationOfConfinedSpace: '',
            workDescription: '',

            // Hazards & Energized Sources (like JHA)
            hazards: [],
            energizedSources: [],

            // Verifications Section
            verifications,

            // Special Requirements Section
            specialRequirements,
            otherRequirements: [], // Array of { name: '', required: false, verified: false }
            respiratorType: '',
            attendants: [], // Array of { id, name, type }

            // Air Monitoring Section
            airMonitoringEquipment: {
                make: '',
                model: '',
                calibrationDate: '',
                calibratedBy: '',
                equipmentId: ''
            },
            atmosphereCheckedBy: '',
            airMonitoringReadings,
            otherContaminants: [], // Array of { name, pel, check1: {value, time}, check2: {value, time}, check3: {value, time} }

            // Emergency Contact
            emergencyContact: '911',
            emergencyContactOther: '',

            // Authorization Section
            entrySupervisorSignature: '',
            entrySupervisorSignatureDate: ''
        },

        // Metadata
        meta: {
            lastStep: 1,
            submittedAt: null,
            submittedBy: null
        },

        ...overrides
    };
}

/**
 * Ensures all steps have proper structure (for migrations/updates)
 */
export function ensureCsepStructure(record) {
    const skeleton = createCsepSkeleton();

    return {
        ...skeleton,
        ...record,
        stepOne: { ...skeleton.stepOne, ...(record.stepOne || {}) },
        stepTwo: { ...skeleton.stepTwo, ...(record.stepTwo || {}) },
        meta: { ...skeleton.meta, ...(record.meta || {}) }
    };
}
